import { useMemo, useState } from "react";
import { Section } from "./Section";
import { Reveal } from "./Reveal";
import { AssetImage } from "./AssetImage";
import { LeadDialog } from "./LeadDialog";
import { Button } from "../ui/button";
import { ASSETS, PLOTS, PLOT_CATEGORIES } from "../../config/site";

// ---- deterministic row-wrap packing of 83 plots + 10 sheds ----------------
const REGIONS = [
  { x: 55, y: 55, w: 510, h: 205 },
  { x: 680, y: 115, w: 530, h: 137 },
  { x: 680, y: 335, w: 530, h: 150 },
  { x: 215, y: 570, w: 350, h: 212 },
  { x: 680, y: 570, w: 360, h: 90 },
];
const CELL_H = 24;
const PITCH = 29;
const GAP = 6;

const plotW = (p) => {
  if (p.cat === "A") return 60;
  if (p.cat === "B") return 62 + ((p.size - 550) / 450) * 26;
  return 92 + ((p.size - 1000) / 845) * 42;
};

function layoutPlots() {
  const plots = PLOTS.filter((p) => p.kind === "plot");
  const placed = [];
  let ri = 0;
  let r = REGIONS[ri];
  let x = r.x;
  let y = r.y;
  for (const p of plots) {
    const w = plotW(p);
    if (x + w > r.x + r.w) {
      x = r.x;
      y += PITCH;
    }
    while (y + CELL_H > r.y + r.h) {
      ri += 1;
      if (ri >= REGIONS.length) return placed;
      r = REGIONS[ri];
      x = r.x;
      y = r.y;
    }
    placed.push({ ...p, x, y, w, h: CELL_H });
    x += w + GAP;
  }
  return placed;
}

const SHEDS = PLOTS.filter((p) => p.kind === "shed").map((p, i) => {
  const col = i % 5;
  const row = Math.floor(i / 5);
  return { ...p, x: 680 + col * 72, y: 676 + row * 43, w: 64, h: 38 };
});

const FacilityBlock = ({ x, y, w, h, label, sub }) => (
  <g>
    <rect x={x} y={y} width={w} height={h} fill="#2A4D3A" stroke="#C8A974" strokeWidth="1" />
    <text x={x + w / 2} y={y + h / 2 - (sub ? 2 : -4)} textAnchor="middle" fontSize="12" fill="#F7F5F0" fontFamily="Cormorant Garamond, serif" fontWeight="600">
      {label}
    </text>
    {sub && (
      <text x={x + w / 2} y={y + h / 2 + 13} textAnchor="middle" fontSize="8" fill="#C8A974">
        {sub}
      </text>
    )}
  </g>
);

const MasterPlanSvg = ({ placed, selected, onSelect }) => (
  <svg viewBox="0 0 1240 900" className="h-auto w-full min-w-[860px]" data-testid="site-plan-svg">
    <defs>
      <pattern id="soldHatch" width="6" height="6" patternTransform="rotate(45)" patternUnits="userSpaceOnUse">
        <rect width="6" height="6" fill="#A8ADA2" />
        <line x1="0" y1="0" x2="0" y2="6" stroke="#8E938A" strokeWidth="2" />
      </pattern>
    </defs>

    {/* highway */}
    <rect x="0" y="845" width="1240" height="45" fill="#2A4D3A" />
    <text x="620" y="872" textAnchor="middle" fontSize="14" letterSpacing="4" fill="#F7F5F0">
      BHIWANI – LOHARU ROAD · NH-709
    </text>

    {/* boundary */}
    <rect x="30" y="30" width="1180" height="800" fill="#F2EEE4" stroke="#C8A974" strokeWidth="2" strokeDasharray="10 6" />

    {/* roads */}
    <rect x="590" y="30" width="60" height="800" fill="#D9D2C2" />
    <rect x="30" y="278" width="1180" height="32" fill="#D9D2C2" />
    <rect x="30" y="508" width="1180" height="32" fill="#D9D2C2" />
    <text x="620" y="820" textAnchor="middle" fontSize="9" fill="#8a8577" transform="rotate(-90 620 430)">
      60.5 FT RCC ROAD
    </text>
    <text x="620" y="296" textAnchor="middle" fontSize="9" fill="#8a8577" letterSpacing="3">
      60.5 FT RCC ROAD
    </text>
    <text x="620" y="526" textAnchor="middle" fontSize="9" fill="#8a8577" letterSpacing="3">
      60.5 FT RCC ROAD
    </text>

    {/* gate */}
    <rect x="596" y="815" width="48" height="34" fill="#C8A974" />
    <text x="620" y="808" textAnchor="middle" fontSize="10" fontWeight="700" fill="#1C3327">
      MAIN GATE
    </text>

    {/* amenity core */}
    <rect x="55" y="335" width="510" height="150" fill="#1C3327" stroke="#C8A974" strokeWidth="1.5" />
    <text x="310" y="352" textAnchor="middle" fontSize="10" letterSpacing="2" fill="#C8A974">
      THE HEART OF BIDC
    </text>
    <FacilityBlock x={70} y={362} w={150} h={110} label="BDC" sub="Approvals · Subsidy · Finance" />
    <FacilityBlock x={235} y={362} w={150} h={110} label="Exhibition Centre" sub="Buyer–seller meets" />
    <FacilityBlock x={400} y={362} w={150} h={110} label="CFC" sub="Servo injection moulding" />

    {/* facilities */}
    <FacilityBlock x={680} y={55} w={400} h={44} label="Labour Quarters" />
    <FacilityBlock x={55} y={570} w={140} h={90} label="CETP" sub="Industrial effluent" />
    <FacilityBlock x={55} y={672} w={140} h={90} label="STP" sub="Sewage treatment" />
    <FacilityBlock x={1060} y={676} w={150} h={86} label="Fire Station" sub="5 lakh litre storage" />
    <circle cx="1155" cy="77" r="22" fill="#2A4D3A" stroke="#C8A974" />
    <text x="1155" y="80" textAnchor="middle" fontSize="8" fill="#F7F5F0">
      WATER
    </text>

    {/* trees */}
    {[90, 200, 310, 420].map((tx) => (
      <circle key={tx} cx={tx} cy="42" r="7" fill="#4A5D23" opacity="0.7" />
    ))}
    {[700, 800, 900, 1000].map((tx) => (
      <circle key={tx} cx={tx} cy="815" r="7" fill="#4A5D23" opacity="0.7" />
    ))}

    {/* plots */}
    {placed.map((p) => {
      const sold = p.status === "sold";
      const active = selected?.id === p.id;
      return (
        <rect
          key={p.id}
          x={p.x}
          y={p.y}
          width={p.w}
          height={p.h}
          rx="1"
          fill={sold ? "url(#soldHatch)" : PLOT_CATEGORIES[p.cat].color}
          stroke={active ? "#C8A974" : "#F7F5F0"}
          strokeWidth={active ? 2 : 0.75}
          className={`plot-rect ${sold ? "plot-sold" : ""} ${active ? "active" : ""}`}
          data-testid={`plot-${p.id}`}
          onMouseEnter={() => onSelect(p)}
          onClick={() => onSelect(p)}
        >
          <title>
            {`${p.id} · ${p.size} sq yd · ${sold ? "Sold" : "Available"}`}
          </title>
        </rect>
      );
    })}

    {/* sheds */}
    {SHEDS.map((p) => {
      const active = selected?.id === p.id;
      return (
        <g key={p.id}>
          <rect
            x={p.x}
            y={p.y}
            width={p.w}
            height={p.h}
            fill={PLOT_CATEGORIES.S.color}
            stroke={active ? "#C8A974" : "#F7F5F0"}
            strokeWidth={active ? 2 : 0.75}
            className={`plot-rect ${active ? "active" : ""}`}
            data-testid={`plot-${p.id}`}
            onMouseEnter={() => onSelect(p)}
            onClick={() => onSelect(p)}
          >
            <title>{`${p.id} · Plug & play shed · For lease`}</title>
          </rect>
        </g>
      );
    })}
    <text x={860} y="670" textAnchor="middle" fontSize="9" letterSpacing="2" fill="#8a8577">
      PLUG &amp; PLAY SHEDS · LEASE
    </text>
  </svg>
);

const LEGEND = [
  { key: "A", note: "2 plots" },
  { key: "B", note: "31 plots" },
  { key: "C", note: "50 plots" },
  { key: "S", note: "10 sheds" },
];

export const SitePlan = () => {
  const placed = useMemo(layoutPlots, []);
  const [selected, setSelected] = useState(null);

  return (
    <Section
      id="master-plan"
      overline="Master plan"
      title="Walk the estate before it is built"
      sub="Every block below is a real plot. Hover or tap to see its size and indicative price — then enquire about the exact one you want."
    >
      <div className="grid gap-8 lg:grid-cols-2">
        <Reveal>
          <div>
            <AssetImage
              asset={ASSETS.sitePlan}
              alt="BIDC approved site plan"
              className="max-h-[620px] w-full border border-border bg-paper-light object-contain"
              placeholderLabel="Approved site plan"
              placeholderSub="Final sanctioned layout image goes here"
            />
            <p className="mt-3 text-xs text-[#4A4C4A]/70">
              Sanctioned layout with plot demarcation — 60'-6" RCC roads, BDC &amp; Exhibition Centre at the core.
            </p>
          </div>
        </Reveal>

        <Reveal delay={0.1}>
          <div className="border border-border bg-paper-light">
            <div className="overflow-x-auto">
              <MasterPlanSvg placed={placed} selected={selected} onSelect={setSelected} />
            </div>
            <div className="flex flex-wrap items-center gap-x-5 gap-y-2 border-t border-border px-4 py-3">
              {LEGEND.map((l) => (
                <span key={l.key} className="flex items-center gap-2 text-xs text-[#4A4C4A]">
                  <span className="inline-block h-3 w-3" style={{ background: PLOT_CATEGORIES[l.key].color }} />
                  {PLOT_CATEGORIES[l.key].label} · {l.note}
                </span>
              ))}
              <span className="flex items-center gap-2 text-xs text-[#4A4C4A]">
                <span className="inline-block h-3 w-3" style={{ background: "repeating-linear-gradient(45deg,#A8ADA2,#A8ADA2 2px,#8E938A 2px,#8E938A 4px)" }} />
                Sold
              </span>
            </div>
          </div>
        </Reveal>
      </div>

      {/* selected plot detail */}
      <div
        className="mt-6 flex flex-wrap items-center justify-between gap-4 border border-gold/50 bg-gold-pale/50 px-5 py-4"
        data-testid="plot-detail-panel"
      >
        {selected ? (
          <>
            <div>
              <p className="font-display text-xl font-medium text-forest">
                {selected.id}
                {selected.size ? ` · ${selected.size.toLocaleString("en-IN")} sq yd` : " · Plug & play shed"}
              </p>
              <p className="text-sm text-[#4A4C4A]">
                {selected.kind === "shed"
                  ? "Ready shed for lease — move in and start."
                  : selected.status === "sold"
                    ? "This plot is sold. Ask us about similar available plots."
                    : "Status: Available · Pricing on request"}
              </p>
            </div>
            {selected.status !== "sold" && (
              <LeadDialog
                type="plot_enquiry"
                defaults={{
                  plot_id: selected.id,
                  plot_size: selected.size ? `${selected.size} sq yd` : "Plug & play shed (lease)",
                }}
              >
                <Button
                  data-testid="plot-enquire-button"
                  className="bg-forest text-paper hover:bg-forest-light transition-colors duration-200"
                >
                  Enquire about this plot
                </Button>
              </LeadDialog>
            )}
          </>
        ) : (
          <p className="text-sm text-[#4A4C4A]">
            Hover or tap any plot on the plan to see its size, price and availability.
          </p>
        )}
      </div>

      <Reveal delay={0.15}>
        <div className="mt-8 flex flex-wrap gap-2.5">
          {[
            "60.5 ft RCC internal roads on a grid",
            "BDC + Exhibition Centre + CFC at the heart",
            "Labour quarters inside campus",
            "Fire station & water tank",
            "CETP & STP inside boundary",
            "Main gate on NH-709",
          ].map((t) => (
            <span key={t} className="border border-border bg-paper-light px-3.5 py-1.5 text-xs text-[#4A4C4A]">
              {t}
            </span>
          ))}
        </div>
      </Reveal>
    </Section>
  );
};
